import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30s cap so a hung/slow request can never spin forever; uploads override this
});

// Bearer fallback: on custom domains the httpOnly session cookie can be blocked/delayed
// (Safari ITP / SameSite=None partitioning), which made users refresh several times to log in.
// We also stash the session_token in localStorage and send it as a Bearer header so auth works
// regardless of cookie behaviour. The cookie still works wherever the browser allows it.
export const AUTH_TOKEN_KEY = "bf_session_token";
export const setAuthToken = (t) => { try { t ? localStorage.setItem(AUTH_TOKEN_KEY, t) : localStorage.removeItem(AUTH_TOKEN_KEY); } catch (e) {} };
export const getAuthToken = () => { try { return localStorage.getItem(AUTH_TOKEN_KEY); } catch (e) { return null; } };
// The persistent owner credential is an HttpOnly cookie ONLY (never in JSON, never in storage). This key is legacy from
// Iter125 and is only ever REMOVED here so an old value can never linger in the browser.
export const LEGACY_PERSIST_KEY = "bf_persist_token";
export const storeAuthTokens = (data) => { if (data?.session_token) setAuthToken(data.session_token); try { localStorage.removeItem(LEGACY_PERSIST_KEY); } catch (e) {} };

api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ONE in-flight /auth/refresh at a time (Iter126A): the persistent owner credential is consumed atomically server-side, so a second
// concurrent restore (StrictMode double effect, two components hitting 401 together) must join the first instead of racing it.
// A plain axios instance is used so the response interceptor below can never recurse into itself.
let refreshInflight = null;
export const refreshSession = () => {
  if (!refreshInflight) {
    refreshInflight = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, timeout: 30000 })
      .then((r) => { storeAuthTokens(r.data); return r.data; })
      .finally(() => { refreshInflight = null; });
  }
  return refreshInflight;
};
// A refresh answered 409 means another refresh rotated the credential a moment ago — the browser already holds the successor
// cookie and localStorage the successor Bearer; treat as "restored elsewhere", never as a sign-out.
export const isRefreshRace = (e) => e?.response?.status === 409;

// Mid-use expiry (Iter126A): when the 7-day short session lapses while the owner is using the app, any authenticated call answered 401 is
// transparently retried once after a silent refresh from the persistent cookie. Auth endpoints themselves are never retried (no loops).
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config;
  const url = String(cfg?.url || "");
  if (error?.response?.status !== 401 || !cfg || cfg._bfRetried || url.includes("/auth/")) return Promise.reject(error);
  try { await refreshSession(); } catch (e) { if (!isRefreshRace(e)) return Promise.reject(error); }
  cfg._bfRetried = true;
  const t = getAuthToken();
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return api.request(cfg);
});

export const fileUrl = (ref) => `${API}/files/${ref}`;   // ref = public media reference `m/<id>` (or a real storage key for admin-only raw access)

// Fast, server-resized image for UI grids/thumbs/viewer (originals stay full-res for downloads).
export const thumbUrl = (ref, w = 600) => `${API}/files/${ref}?w=${w}`;

// Video playback + poster: use the bf H.264 delivery URLs (browser-safe), fall back to local.
// A video is only browser-playable once the bf pipeline has produced an H.264 URL. We NEVER fall back
// to the raw uploaded file (iPhone .mov/HEVC is undecodable in most browsers → black/slow).
export const videoUrl = (item) => (item && item.cloud_video_url) || "";
// Video poster: derived JPEG only — never the raw .mov. (Images may still use the file poster.)
export const posterUrl = (item, w = 800) => {
  if (item && item.cloud_poster_url) return item.cloud_poster_url;
  if (item && item.media_type === "video") return undefined;    // no raw-MOV poster fallback (undefined => attribute omitted, no empty src)
  return item ? `${API}/files/${item.file_ref}?poster=1&w=${w}` : undefined;
};
// True while a video has no playable H.264 URL yet (show a "Processing…" state, not the raw file).
export const videoProcessing = (item) => !!item && item.media_type === "video" && !item.cloud_video_url;

// Watermarked (server-stamped) version of an image — used for large/downloadable views.
export const wmUrl = (ref) => `${API}/files/${ref}?wm=1`;

export const resolveAvatar = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : fileUrl(url);
};

// Titles auto-default to the uploaded filename — hide those from the UI.
const _DEVICE_RE = /^(img|image|dsc|dscf|pxl|vid|video|mvimg|mov|gopro|dji|photo|capture|snapchat|whatsapp|fb[_-]?img|signal|inshot|received|burst|wp)[-_ ]?\d{2,}/i;
const _SHOT_RE = /^screen[ _-]?shot|^screenshot/i;
const _EXT_RE = /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?|mp4|mov|m4v|avi|mkv|webm|3gp)$/i;
export const isFilenameLike = (t) => {
  if (!t) return true;
  const s = String(t).trim();
  if (!s) return true;
  if (_EXT_RE.test(s) || _DEVICE_RE.test(s) || _SHOT_RE.test(s)) return true;
  if (/^[0-9a-f]{8,}$/i.test(s.replace(/[-_\s]/g, ""))) return true; // uuid / hex blob
  if (/^\d+$/.test(s)) return true; // pure number
  if (!/\s/.test(s) && s.length >= 12 && s.split(/[-_]+/).length >= 3 && /[-_]/.test(s)) return true; // export slug e.g. wrinkled_soles_by__daluba_di8oy47-pre
  return false;
};
// Returns a display-safe title, or "" when the title is just a filename.
export const displayTitle = (t) => (isFilenameLike(t) ? "" : String(t).trim());
